import { useEffect, useMemo, useState } from 'react';
import {
  addLeadNote,
  assignLead,
  claimLead,
  createFollowup,
  fetchLead,
  fetchLeadFollowups,
  fetchTelecallers,
  fetchWaTemplates,
  getTelecallerPhone,
  initCall,
  sendWhatsapp,
  setTelecallerPhone,
  updateFollowupStatus,
  updateLeadName,
  updateStatus,
  updateTier,
} from '../api';
import type { CallLog, Followup, Lead, LeadEvent, LeadStatus, Telecaller, WaTemplate } from '../types';
import { STATUSES, STATUS_COLOR, STATUS_LABEL } from '../types';

function waHref(phone: string, opening: string) {
  return `https://wa.me/${phone.replace(/[^\d]/g, '')}?text=${encodeURIComponent(opening)}`;
}

function fmtDuration(s: number) {
  if (!s) return '0s';
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m ? `${m}m ${r}s` : `${r}s`;
}

function eventLabel(kind: string) {
  const map: Record<string, string> = {
    created: 'Lead created',
    inbound_call: 'Inbound call',
    call_initiated: 'Outbound call started',
    call_completed: 'Call ended',
    status_change: 'Status changed',
    whatsapp_sent: 'WhatsApp sent',
    assigned: 'Assigned',
    name_updated: 'Name updated',
    note: 'Note',
    followup_scheduled: 'Follow-up scheduled',
    followup_completed: 'Follow-up completed',
    followup_cancelled: 'Follow-up cancelled',
  };
  return map[kind] ?? kind;
}

function findNeoDoveProperty(answers: Record<string, unknown>, key: string): unknown {
  const lists = Array.isArray(answers.other_properties) ? answers.other_properties : [];

  for (const list of lists) {
    if (!list || typeof list !== 'object') continue;
    const properties = (list as { properties?: unknown }).properties;
    if (!Array.isArray(properties)) continue;

    const match = properties.find((property) => {
      if (!property || typeof property !== 'object') return false;
      return (property as { name?: unknown }).name === key;
    });

    if (match && typeof match === 'object') {
      return (match as { value?: unknown }).value;
    }
  }

  return undefined;
}

function asDisplayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return '';
  if (typeof value === 'object') return '';
  return String(value);
}

function formatNeoDoveAnswers(rawAnswers: string | null) {
  if (!rawAnswers) return [];

  try {
    const answers = JSON.parse(rawAnswers) as Record<string, unknown>;
    const property = (key: string) => findNeoDoveProperty(answers, key);
    const rows: Array<[string, unknown]> = [
      ['Source', answers.source || answers.provider],
      ['Event', answers.event_name],
      ['Mobile', answers.mobile || answers.phone],
      ['Lead status', answers.lead_status_name || answers.lead_stage_name],
      ['Call status', property('status')],
      ['Direction', property('direction')],
      ['Virtual number', property('virtualnumber')],
      ['Time', property('timeFormatted') || answers.time_formatted],
      ['Agent', answers.agent_name],
      ['Campaign ID', answers.campaign_id],
      ['NeoDove lead ID', answers.lead_id],
    ];

    return rows
      .map(([label, value]) => ({ label, value: asDisplayValue(value) }))
      .filter((row) => row.value);
  } catch {
    return [];
  }
}

type ReasonOption = {
  code: string;
  label: string;
};

const LOST_REASONS: ReasonOption[] = [
  { code: 'did_not_pick', label: 'Did not pick' },
  { code: 'busy_in_another_call', label: 'Busy in another call' },
  { code: 'user_disconnected', label: 'User disconnected the call' },
  { code: 'switch_off', label: 'Switch off' },
  { code: 'out_of_coverage', label: 'Out of Coverage area / Network issue' },
  { code: 'call_not_connected', label: 'Call not connected / can not be completed' },
  { code: 'other_reason', label: 'Other reason' },
  { code: 'incorrect_invalid_number', label: 'Incorrect / Invalid number' },
  { code: 'incoming_calls_not_available', label: 'Incoming calls not available' },
  {
    code: 'number_not_in_use',
    label: 'Number not in use / does not exists / out of service',
  },
];

const WON_REASONS: ReasonOption[] = [
  { code: 'booked_consult', label: 'Booked consult' },
  { code: 'booked_procedure', label: 'Booked procedure' },
  { code: 'paid_advance', label: 'Paid advance' },
  { code: 'referral', label: 'Referral to clinic' },
];

function WonReasonModal({
  open,
  busy,
  selectedCode,
  customReason,
  onSelect,
  onCustomReasonChange,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  selectedCode: string;
  customReason: string;
  onSelect: (code: string) => void;
  onCustomReasonChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  const canSubmit = !!selectedCode || customReason.trim().length >= 3;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl sm:p-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h3 className="text-base font-semibold text-slate-900">Why won?</h3>
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-sm text-slate-500 active:text-slate-800 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {WON_REASONS.map((reason) => {
            const active = reason.code === selectedCode;
            return (
              <button
                key={reason.code}
                onClick={() => onSelect(active ? '' : reason.code)}
                disabled={busy}
                className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                  active ? 'bg-slate-200 text-slate-700' : 'bg-slate-100 text-slate-600'
                } disabled:opacity-60`}
              >
                {reason.label}
              </button>
            );
          })}
        </div>

        <div className="mt-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Or type a custom reason
          </div>
          <input
            value={customReason}
            onChange={(e) => onCustomReasonChange(e.target.value)}
            disabled={busy}
            placeholder="e.g. husband working abroad"
            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-slate-400 focus:outline-none disabled:bg-slate-50"
          />
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || !canSubmit}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Mark Won'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReasonPickerModal({
  open,
  busy,
  selectedCode,
  customReason,
  title,
  confirmLabel,
  confirmTone,
  alwaysShowCustomReason = false,
  onSelect,
  onCustomReasonChange,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  selectedCode: string;
  customReason: string;
  title: string;
  confirmLabel: string;
  confirmTone: string;
  alwaysShowCustomReason?: boolean;
  onSelect: (code: string) => void;
  onCustomReasonChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  const canConfirm =
    !!selectedCode && (selectedCode !== 'other_reason' || customReason.trim().length > 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        </div>

        <div className="max-h-[55vh] overflow-y-auto px-5 py-4">
          <div className="space-y-2">
            {LOST_REASONS.map((reason) => {
              const active = reason.code === selectedCode;
              return (
                <div key={reason.code} className="space-y-2">
                  <label
                    className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 transition ${
                      active
                        ? 'border-slate-900 bg-slate-50'
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                    } ${busy ? 'opacity-60' : ''}`}
                  >
                    <input
                      type="radio"
                      checked={active}
                      onChange={() => onSelect(reason.code)}
                      disabled={busy}
                      className="h-5 w-5 border-slate-400 text-slate-900 focus:ring-slate-900"
                    />
                    <span className="flex-1 text-sm text-slate-800">{reason.label}</span>
                  </label>
                  {reason.code === 'other_reason' && active && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Add reason
                      </label>
                      <textarea
                        value={customReason}
                        onChange={(e) => onCustomReasonChange(e.target.value)}
                        disabled={busy}
                        rows={3}
                        placeholder="Type the reason here"
                        className="mt-2 w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none disabled:bg-slate-100"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || !canConfirm}
            className={`rounded-lg px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50 ${confirmTone}`}
          >
            {busy ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LeadDetail({
  leadId,
  me,
  userRole,
  onClose,
  onChanged,
  inline = false,
}: {
  leadId: string;
  me: string;
  userRole: string;
  onClose: () => void;
  onChanged: () => void;
  /** When true, renders as an inline right-pane (desktop split-view) instead of a bottom drawer. */
  inline?: boolean;
}) {
  const [lead, setLead] = useState<Lead | null>(null);
  const [events, setEvents] = useState<LeadEvent[]>([]);
  const [calls, setCalls] = useState<CallLog[]>([]);
  const [followups, setFollowups] = useState<Followup[]>([]);
  const [telecallers, setTelecallers] = useState<Telecaller[]>([]);
  const [templates, setTemplates] = useState<WaTemplate[]>([]);
  const [selectedTpl, setSelectedTpl] = useState<string>('');
  const [tplParams, setTplParams] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [wonReasonOpen, setWonReasonOpen] = useState(false);
  const [wonReasonCode, setWonReasonCode] = useState('');
  const [wonReasonText, setWonReasonText] = useState('');
  const [lostReasonOpen, setLostReasonOpen] = useState(false);
  const [lostReasonCode, setLostReasonCode] = useState('');
  const [lostReasonText, setLostReasonText] = useState('');
  const [busyName, setBusyName] = useState(false);
  const [busyCall, setBusyCall] = useState(false);
  const [busyWa, setBusyWa] = useState(false);
  const [phone, setPhone] = useState(getTelecallerPhone());
  const [customerName, setCustomerName] = useState('');
  const [noteText, setNoteText] = useState('');
  const [busyNote, setBusyNote] = useState(false);
  
  // Follow-up form state
  const [showFollowupForm, setShowFollowupForm] = useState(false);
  const [followupDate, setFollowupDate] = useState('');
  const [followupTime, setFollowupTime] = useState('');
  const [followupNotes, setFollowupNotes] = useState('');
  const [busyFollowup, setBusyFollowup] = useState(false);
  const neodoveDetails = useMemo(
    () => (lead?.source === 'neodove' ? formatNeoDoveAnswers(lead.raw_answers) : []),
    [lead?.source, lead?.raw_answers]
  );

  async function load() {
    try {
      const [l, tc, tpl, fu] = await Promise.all([
        fetchLead(leadId),
        fetchTelecallers().catch(() => ({ telecallers: [] as Telecaller[] })),
        fetchWaTemplates().catch(() => ({ templates: [] as WaTemplate[] })),
        fetchLeadFollowups(leadId).catch(() => ({ followups: [] as Followup[] })),
      ]);
      setLead(l.lead);
      setCustomerName(l.lead.name || '');
      setEvents(l.events);
      setCalls(l.calls);
      setTelecallers(tc.telecallers);
      setTemplates(tpl.templates);
      setFollowups(fu.followups);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'load failed');
    }
  }

  useEffect(() => {
    load();
  }, [leadId]);

  async function changeStatus(next: LeadStatus) {
    if (!lead || next === lead.status) return;
    if (blockIfLocked()) return;
    if (next === 'won') {
      setWonReasonOpen(true);
      setWonReasonCode('');
      setWonReasonText('');
      return;
    }
    if (next === 'lost') {
      setLostReasonOpen(true);
      setLostReasonCode('');
      setLostReasonText('');
      return;
    }
    setBusy(true);
    try {
      await updateStatus(lead.id, next);
      await load();
      onChanged();
    } catch (e) {
      alert(`Status change failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusy(false);
    }
  }

  async function confirmLost() {
    if (blockIfLocked()) return;
    if (!lead || !lostReasonCode) return;
    const selected = LOST_REASONS.find((reason) => reason.code === lostReasonCode);
    const reasonText = lostReasonCode === 'other_reason' ? lostReasonText.trim() : selected?.label;
    if (lostReasonCode === 'other_reason' && !reasonText) return;
    setBusy(true);
    try {
      await updateStatus(lead.id, 'lost', {
        reason_code: lostReasonCode,
        reason_text: reasonText,
      });
      setLostReasonOpen(false);
      setLostReasonCode('');
      setLostReasonText('');
      await load();
      onChanged();
    } catch (e) {
      alert(`Status change failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusy(false);
    }
  }

  async function confirmWon() {
    if (blockIfLocked()) return;
    if (!lead || (!wonReasonCode && !wonReasonText.trim())) return;
    const selected = WON_REASONS.find((reason) => reason.code === wonReasonCode);
    const reasonText = wonReasonText.trim() || selected?.label;
    setBusy(true);
    try {
      await updateStatus(lead.id, 'won', {
        reason_code: wonReasonCode || undefined,
        reason_text: reasonText,
      });
      setWonReasonOpen(false);
      setWonReasonCode('');
      setWonReasonText('');
      await load();
      onChanged();
    } catch (e) {
      alert(`Status change failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusy(false);
    }
  }

  async function changeTier(next: 1 | 2 | 3) {
    if (!lead || next === lead.tier_score) return;
    if (blockIfLocked()) return;
    setBusy(true);
    try {
      await updateTier(lead.id, next);
      await load();
      onChanged();
    } catch (e) {
      alert(`Tier change failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusy(false);
    }
  }

  async function assign(email: string | null) {
    if (!lead) return;
    setBusy(true);
    try {
      if (isTelecaller && email === me && !lead.assigned_to) {
        await claimLead(lead.id);
      } else {
        await assignLead(lead.id, email);
      }
      await load();
      onChanged();
    } catch (e) {
      alert(`Assign failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusy(false);
    }
  }

  async function saveCustomerName() {
    if (!lead) return;
    if (blockIfLocked()) return;
    const name = customerName.trim();
    if (!name) {
      alert('Customer name cannot be empty');
      return;
    }
    if (name === lead.name) return;

    setBusyName(true);
    try {
      await updateLeadName(lead.id, name);
      await load();
      onChanged();
    } catch (e) {
      alert(`Name update failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusyName(false);
    }
  }

  async function call() {
    if (!lead) return;
    if (blockIfLocked()) return;
    if (!phone) {
      alert('Enter your phone first — Vobiz will ring you then bridge the patient.');
      return;
    }
    setBusy(true);
    try {
      await initCall(lead.id, phone);
      await load();
      onChanged();
    } catch (e) {
      alert(`Call failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusy(false);
    }
  }

  const currentTemplate = useMemo(
    () => templates.find((t) => t.name === selectedTpl),
    [templates, selectedTpl]
  );

  function pickTemplate(name: string) {
    setSelectedTpl(name);
    const tpl = templates.find((t) => t.name === name);
    if (!tpl) {
      setTplParams([]);
      return;
    }
    // Smart defaults for common param slots
    setTplParams(
      tpl.params.map((label) => {
        const l = label.toLowerCase();
        if (l.includes('patient') || l.includes('name')) return lead?.name ?? '';
        if (l.includes('clinic')) return 'Santaan & SKIDS';
        if (l.includes('telecaller')) return me.split('@')[0];
        return '';
      })
    );
  }

  async function sendTemplate() {
    if (blockIfLocked()) return;
    if (!lead || !selectedTpl) return;
    setBusyWa(true);
    try {
      const res = await sendWhatsapp(lead.id, {
        template_name: selectedTpl,
        params: tplParams,
      });
      await load();
      onChanged();
      alert(`Sent via ${res.provider ?? 'API'}. Response: ${res.raw ?? 'ok'}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown';
      if (msg.includes('501')) {
        alert('WhatsApp provider not configured on worker.');
      } else {
        alert(`Send failed: ${msg}`);
      }
    } finally {
      setBusyWa(false);
    }
  }

  function savePhone(v: string) {
    setPhone(v);
    setTelecallerPhone(v);
  }

  async function handleCreateFollowup() {
    if (blockIfLocked()) return;
    if (!followupDate) {
      alert('Please select a date for the follow-up');
      return;
    }
    if (!lead) return;
    
    setBusyFollowup(true);
    try {
      await createFollowup(lead.id, {
        followup_date: followupDate,
        followup_time: followupTime || undefined,
        notes: followupNotes || undefined,
      });
      await load();
      onChanged();
      setShowFollowupForm(false);
      setFollowupDate('');
      setFollowupTime('');
      setFollowupNotes('');
    } catch (e) {
      alert(`Failed to create follow-up: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusyFollowup(false);
    }
  }

  async function addNote() {
    if (!lead) return;
    if (blockIfLocked()) return;
    const note = noteText.trim();
    if (!note) return;

    setBusyNote(true);
    try {
      await addLeadNote(lead.id, note);
      setNoteText('');
      await load();
      onChanged();
    } catch (e) {
      alert(`Note failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusyNote(false);
    }
  }

  async function handleCompleteFollowup(followupId: string) {
    if (blockIfLocked()) return;
    if (!confirm('Mark this follow-up as completed?')) return;
    
    try {
      await updateFollowupStatus(followupId, 'completed');
      await load();
      onChanged();
    } catch (e) {
      alert(`Failed to update follow-up: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  const mine = lead?.assigned_to === me;
  const isTelecaller = userRole === 'telecaller';
  const lockedForTelecaller = !!lead && isTelecaller && !mine;
  const previousAssignee =
    lead?.last_assigned_to && lead.last_assigned_to !== lead.assigned_to
      ? lead.last_assigned_to.split('@')[0]
      : null;
  const blockedMessage = lead?.assigned_to
    ? 'This lead is locked to another telecaller.'
    : 'Assign this lead to yourself before making changes.';

  function blockIfLocked() {
    if (!lockedForTelecaller) return false;
    alert(blockedMessage);
    return true;
  }

  const detailInner = (
    <>
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <h2 className="text-base font-semibold">{lead?.name ?? 'Loading…'}</h2>
        <button onClick={onClose} className="text-sm text-slate-500 active:text-slate-800">
          Close
        </button>
      </div>
      <div
        className={
          inline
            ? 'flex-1 space-y-4 overflow-y-auto px-4 py-4'
            : 'max-h-[80vh] space-y-4 overflow-y-auto px-4 py-4 pb-[env(safe-area-inset-bottom,16px)]'
        }
      >
          {lockedForTelecaller && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {blockedMessage}
            </div>
          )}
          {err && <p className="rounded-lg bg-red-50 p-2 text-xs text-red-700">{err}</p>}
          {!lead && !err && <p className="text-center text-sm text-slate-500">Loading…</p>}

          {lead && (
            <>
              <section className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1 pr-3">
                    <div className="text-xs text-slate-500">Customer name</div>
                    <div className="mt-1 flex gap-2">
                      <input
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        onBlur={saveCustomerName}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        disabled={busyName || lockedForTelecaller}
                        placeholder="Add customer name"
                        className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      />
                      <button
                        onClick={saveCustomerName}
                        disabled={busyName || customerName.trim() === lead.name || lockedForTelecaller}
                        className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                  <span
                    className={`flex-none rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLOR[lead.status]}`}
                  >
                    {STATUS_LABEL[lead.status]}
                  </span>
                </div>
                <div className="mt-3 flex items-start justify-between">
                  <div>
                    <div className="text-xs text-slate-500">Phone</div>
                    <div className="text-sm font-medium text-slate-900">{lead.phone}</div>
                  </div>
                </div>
                <div className="mt-2 text-xs text-slate-500">Created</div>
                <div className="text-xs text-slate-700">{lead.created_at}</div>
                {lead.raw_answers && lead.source === 'neodove' && neodoveDetails.length > 0 && (
                  <>
                    <div className="mt-2 text-xs text-slate-500">Details</div>
                    <div className="mt-1 rounded-lg bg-white p-2 text-xs">
                      {neodoveDetails.map((row) => (
                        <div key={row.label} className="flex justify-between gap-3 py-1">
                          <span className="text-slate-500">{row.label}</span>
                          <span className="break-words text-right font-medium text-slate-800">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {lead.raw_answers && !(lead.source === 'neodove' && neodoveDetails.length > 0) && (
                  <>
                    <div className="mt-2 text-xs text-slate-500">Answers</div>
                    <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-white p-2 text-[11px] text-slate-700">
                      {(() => {
                        try {
                          return JSON.stringify(JSON.parse(lead.raw_answers), null, 2);
                        } catch {
                          return lead.raw_answers;
                        }
                      })()}
                    </pre>
                  </>
                )}
              </section>

              <section>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  AI insight
                </div>
                <div className="rounded-2xl rounded-tl-sm bg-wechat-bubble px-3 py-2 text-sm">
                  {lead.opening_line}
                </div>
                <p className="mt-2 text-xs italic text-slate-600">Tip: {lead.persuasion_tip}</p>
              </section>

              <section>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Assignment
                  </div>
                </div>
                {isTelecaller ? (
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">
                        {mine ? 'Assigned to you' : lead.assigned_to ? 'Assigned to someone else' : 'Not assigned yet'}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {mine
                          ? 'You can use all lead actions.'
                          : lead.assigned_to
                          ? 'This lead is locked to another telecaller.'
                          : 'You can view it, then claim it to unlock actions.'}
                      </div>
                      {!lead.assigned_to && previousAssignee && (
                        <div className="mt-1 text-[11px] text-slate-500">
                          Previously handled by <span className="font-semibold text-slate-700">{previousAssignee}</span>
                        </div>
                      )}
                    </div>
                    {!mine && !lead.assigned_to ? (
                      <button
                        onClick={() => assign(me)}
                        disabled={busy}
                        className="flex-none rounded-full bg-emerald-100 px-3 py-1.5 text-[11px] font-semibold text-emerald-700 disabled:opacity-60"
                      >
                        Assign to me
                      </button>
                    ) : !mine && lead.assigned_to ? (
                      <span className="flex-none rounded-full bg-slate-100 px-3 py-1.5 text-[11px] font-semibold text-slate-600">
                        No access
                      </span>
                    ) : (
                      <span className="flex-none rounded-full bg-emerald-100 px-3 py-1.5 text-[11px] font-semibold text-emerald-700">
                        Assigned
                      </span>
                    )}
                  </div>
                ) : (
                  <select
                    value={lead.assigned_to ?? ''}
                    onChange={(e) => assign(e.target.value || null)}
                    disabled={busy}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="">Unassigned</option>
                    {telecallers.map((t) => (
                      <option key={t.email} value={t.email}>
                        {t.name ? `${t.name} (${t.email})` : t.email}
                      </option>
                    ))}
                    {lead.assigned_to && !telecallers.find((t) => t.email === lead.assigned_to) && (
                      <option value={lead.assigned_to}>{lead.assigned_to}</option>
                    )}
                  </select>
                )}
              </section>

              <section>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Status
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {STATUSES.map((s) => {
                    const active = s === lead.status;
                    return (
                      <button
                        key={s}
                        onClick={() => changeStatus(s)}
                        disabled={busy || active || lockedForTelecaller}
                        className={`rounded-full px-3 py-1 text-xs font-semibold disabled:opacity-60 ${
                          active ? STATUS_COLOR[s] : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {STATUS_LABEL[s]}
                      </button>
                    );
                  })}
                </div>
              </section>

              {wonReasonOpen && (
                <WonReasonModal
                  open={wonReasonOpen}
                  busy={busy}
                  selectedCode={wonReasonCode}
                  customReason={wonReasonText}
                  onSelect={setWonReasonCode}
                  onCustomReasonChange={setWonReasonText}
                  onCancel={() => {
                    if (!busy) {
                      setWonReasonOpen(false);
                      setWonReasonCode('');
                      setWonReasonText('');
                    }
                  }}
                  onConfirm={confirmWon}
                />
              )}

              {lostReasonOpen && (
                <ReasonPickerModal
                  open={lostReasonOpen}
                  busy={busy}
                  selectedCode={lostReasonCode}
                  customReason={lostReasonText}
                  title="Please specify the reason?"
                  confirmLabel="Mark Lost"
                  confirmTone="bg-rose-600"
                  onSelect={setLostReasonCode}
                  onCustomReasonChange={setLostReasonText}
                  onCancel={() => {
                    if (!busy) {
                      setLostReasonOpen(false);
                      setLostReasonCode('');
                      setLostReasonText('');
                    }
                  }}
                  onConfirm={confirmLost}
                />
              )}

              <section>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Lead Tier (Hot/Warm/Cold)
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {[1, 2, 3].map((t) => {
                    const active = t === lead.tier_score;
                    const tierLabels: Record<number, string> = {
                      1: '🔥 Hot',
                      2: '🌡️ Warm',
                      3: '❄️ Cold',
                    };
                    const tierColors: Record<number, string> = {
                      1: 'bg-red-100 text-red-800',
                      2: 'bg-amber-100 text-amber-800',
                      3: 'bg-blue-100 text-blue-800',
                    };
                    return (
                      <button
                        key={t}
                        onClick={() => changeTier(t as 1 | 2 | 3)}
                        disabled={busy || active || lockedForTelecaller}
                        className={`rounded-full px-3 py-1 text-xs font-semibold disabled:opacity-60 ${
                          active ? tierColors[t] : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {tierLabels[t]}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-[11px] italic text-slate-600">
                  Tier 1 = Hot/Urgent, Tier 2 = Warm/Informational, Tier 3 = Cold/Low priority
                </p>
              </section>

              <section>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Call
                </div>
                <input
                  value={phone}
                  onChange={(e) => savePhone(e.target.value)}
                  placeholder="Your phone (E.164, +9198…)"
                  inputMode="tel"
                  disabled={lockedForTelecaller}
                  className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100"
                />
                <div className="flex gap-2">
                  <a
                    href={waHref(lead.phone, lead.opening_line)}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => {
                      if (lockedForTelecaller) {
                        e.preventDefault();
                        blockIfLocked();
                      }
                    }}
                    className={`flex-1 rounded-lg px-3 py-2 text-center text-sm font-semibold text-white active:scale-[0.98] ${
                      lockedForTelecaller ? 'cursor-not-allowed bg-slate-400' : 'bg-wechat-accent'
                    }`}
                  >
                    Open WhatsApp
                  </a>
                  <button
                    onClick={call}
                    disabled={busyCall || lockedForTelecaller}
                    className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60 active:scale-[0.98]"
                  >
                    {busyCall ? 'Dialing…' : 'Call via Vobiz'}
                  </button>
                </div>
              </section>

              <section>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    WhatsApp template
                  </div>
                  <span className="text-[10px] text-slate-400">
                    {templates.filter((t) => t.status === 'APPROVED').length} approved ·{' '}
                    {templates.filter((t) => t.status === 'PENDING').length} pending
                  </span>
                </div>
                  <select
                    value={selectedTpl}
                    onChange={(e) => pickTemplate(e.target.value)}
                    disabled={lockedForTelecaller}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100"
                  >
                  <option value="">— pick a template —</option>
                  <optgroup label="Approved">
                    {templates
                      .filter((t) => t.status === 'APPROVED')
                      .map((t) => (
                        <option key={t.name} value={t.name}>
                          {t.label} · {t.category}
                          {t.params.length ? ` · ${t.params.length}p` : ''}
                        </option>
                      ))}
                  </optgroup>
                  <optgroup label="Pending Meta approval">
                    {templates
                      .filter((t) => t.status === 'PENDING')
                      .map((t) => (
                        <option key={t.name} value={t.name} disabled>
                          {t.label} · {t.category} · PENDING
                        </option>
                      ))}
                  </optgroup>
                </select>

                {currentTemplate && (
                  <>
                    {currentTemplate.note && (
                      <p className="mt-1.5 text-[11px] italic text-amber-700">
                        ⚠ {currentTemplate.note}
                      </p>
                    )}
                    {currentTemplate.params.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {currentTemplate.params.map((label, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="w-5 flex-none text-[11px] font-mono text-slate-500">
                              {`{{${i + 1}}}`}
                            </span>
                            <input
                              value={tplParams[i] ?? ''}
                              onChange={(e) => {
                                const next = [...tplParams];
                                next[i] = e.target.value;
                                setTplParams(next);
                              }}
                              placeholder={label}
                              disabled={lockedForTelecaller}
                              className="flex-1 rounded border border-slate-300 px-2 py-1 text-xs disabled:bg-slate-100"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={sendTemplate}
                      disabled={
                        busyWa ||
                        currentTemplate.status !== 'APPROVED' ||
                        currentTemplate.params.some((_, i) => !tplParams[i]?.trim()) ||
                        lockedForTelecaller
                      }
                      className="mt-2 w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 active:scale-[0.98]"
                    >
                      {busyWa ? 'Sending…' : `Send ${currentTemplate.name} → ${lead.phone}`}
                    </button>
                  </>
                )}
              </section>

              {calls.length > 0 && (
                <section>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Call logs ({calls.length})
                  </div>
                  <ul className="space-y-1.5">
                    {calls.map((cl) => (
                      <li
                        key={cl.id}
                        className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs"
                      >
                        <span className="text-slate-700">
                          {cl.created_at}
                          {cl.initiated_by && (
                            <span className="ml-1 text-slate-500">
                              · {cl.initiated_by.split('@')[0]}
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="font-semibold">{fmtDuration(cl.duration)}</span>
                          {cl.recording_url && (
                            <a
                              href={cl.recording_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 underline"
                            >
                              recording
                            </a>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Follow-ups
                  </div>
                  <button
                    onClick={() => setShowFollowupForm(!showFollowupForm)}
                    disabled={lockedForTelecaller}
                    className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-60"
                  >
                    {showFollowupForm ? 'Cancel' : '+ Schedule'}
                  </button>
                </div>

                {showFollowupForm && (
                  <div className="mb-3 rounded-lg bg-slate-50 p-3 space-y-2">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Date *
                      </label>
                      <input
                        type="date"
                        value={followupDate}
                        onChange={(e) => setFollowupDate(e.target.value)}
                        disabled={lockedForTelecaller}
                        className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Time (optional)
                      </label>
                      <input
                        type="time"
                        value={followupTime}
                        onChange={(e) => setFollowupTime(e.target.value)}
                        disabled={lockedForTelecaller}
                        className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Notes
                      </label>
                      <textarea
                        value={followupNotes}
                        onChange={(e) => setFollowupNotes(e.target.value)}
                        placeholder="What did you discuss? What to follow up on?"
                        rows={2}
                        disabled={lockedForTelecaller}
                        className="w-full resize-none rounded border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
                      />
                    </div>
                    <button
                      onClick={handleCreateFollowup}
                      disabled={busyFollowup || !followupDate || lockedForTelecaller}
                      className="w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 active:scale-[0.98]"
                    >
                      {busyFollowup ? 'Scheduling…' : 'Schedule Follow-up'}
                    </button>
                  </div>
                )}

                {followups.length === 0 && !showFollowupForm && (
                  <p className="text-xs text-slate-500">No follow-ups scheduled.</p>
                )}

                {followups.length > 0 && (
                  <ul className="space-y-1.5">
                    {followups.map((fu) => (
                      <li
                        key={fu.id}
                        className={`rounded-lg px-3 py-2 text-xs ${
                          fu.status === 'completed'
                            ? 'bg-emerald-50 border border-emerald-200'
                            : fu.status === 'cancelled'
                            ? 'bg-slate-100 border border-slate-200 opacity-60'
                            : 'bg-amber-50 border border-amber-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-slate-800">
                              {fu.followup_date}
                              {fu.followup_time && ` at ${fu.followup_time}`}
                            </div>
                            {fu.notes && (
                              <div className="mt-1 text-slate-600">{fu.notes}</div>
                            )}
                            <div className="mt-1 text-[10px] text-slate-500">
                              {fu.created_by.split('@')[0]} · {fu.created_at}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                fu.status === 'completed'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : fu.status === 'cancelled'
                                  ? 'bg-slate-200 text-slate-600'
                                  : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {fu.status}
                            </span>
                            {fu.status === 'pending' && (
                              <button
                                onClick={() => handleCompleteFollowup(fu.id)}
                                className="rounded bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-emerald-700"
                              >
                                Complete
                              </button>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Notes
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add call reason, state, promise, objection, or anything important..."
                    rows={3}
                    disabled={lockedForTelecaller}
                    className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:bg-slate-100"
                  />
                  <button
                    onClick={addNote}
                    disabled={busyNote || !noteText.trim() || lockedForTelecaller}
                    className="mt-2 w-full rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 active:scale-[0.98]"
                  >
                    {busyNote ? 'Adding…' : 'Add note'}
                  </button>
                </div>
              </section>

              <section>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Timeline
                </div>
                {events.length === 0 ? (
                  <p className="text-xs text-slate-500">No events yet.</p>
                ) : (
                  <ul className="space-y-2 border-l border-slate-200 pl-3">
                    {events.map((ev) => {
                      let dataObj: Record<string, unknown> = {};
                      try {
                        dataObj = ev.data ? JSON.parse(ev.data) : {};
                      } catch {}
                      return (
                        <li key={ev.id} className="relative text-xs">
                          <span className="absolute -left-[17px] top-1 h-2 w-2 rounded-full bg-slate-400" />
                          <div className="font-medium text-slate-800">{eventLabel(ev.kind)}</div>
                          <div className="text-slate-500">
                            {ev.created_at}
                            {ev.actor && ev.actor !== 'system' && (
                              <span className="ml-1">· {ev.actor.split('@')[0]}</span>
                            )}
                          </div>
                          {Object.keys(dataObj).length > 0 && (
                            <div className="mt-0.5 text-slate-600">
                              {Object.entries(dataObj)
                                .filter(([, v]) => v !== '' && v !== null && v !== undefined)
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(' · ')}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </>
          )}
      </div>
    </>
  );

  if (inline) {
    return <div className="flex h-full flex-col bg-white">{detailInner}</div>;
  }

  return (
    <div className="fixed inset-0 z-30 bg-black/40" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="absolute inset-x-0 bottom-0 max-h-[92vh] overflow-hidden rounded-t-3xl bg-white shadow-2xl"
      >
        {detailInner}
      </div>
    </div>
  );
}
